/**
 * «Ξεχάσατε τον κωδικό σας;» — asks for an email and reports the same outcome whether or
 * not an account exists there. The server is what guarantees that; this screen just never
 * pretends to know more than the server told it.
 */

import * as React from 'react';
import { Link } from 'react-router-dom';
import { forgotPasswordSchema } from '@ias/shared';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthError, AuthScreen, authInputClassName } from '@/components/layout/auth-screen';
import { ApiRequestError, post } from '@/lib/api/client';
import { useI18n } from '@/lib/i18n';

export function ForgotPasswordPage() {
  const { t } = useI18n();

  const [email, setEmail] = React.useState('');
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    const parsed = forgotPasswordSchema.safeParse({ email: email.trim() });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t('error.generic'));
      return;
    }

    setSubmitting(true);
    try {
      await post('/auth/forgot-password', parsed.data);
      setSent(true);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : t('error.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthScreen>
      {sent ? (
        <div className="space-y-4">
          <p className="text-sm leading-relaxed text-white/85">{t('auth.resetLinkSent')}</p>
          <Button asChild variant="outline" className="w-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">
            <Link to="/">{t('auth.backToSignIn')}</Link>
          </Button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1">
            <h1 className="font-display text-lg text-white">{t('auth.forgotPassword')}</h1>
            <p className="text-[13px] leading-relaxed text-white/65">{t('auth.forgotPasswordHint')}</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-white/80">{t('auth.email')}</Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              required
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-invalid={Boolean(error)}
              className={authInputClassName}
            />
          </div>

          {error && <AuthError>{error}</AuthError>}

          <Button type="submit" className="bg-brand-gradient w-full border-0 shadow-lg shadow-[color:var(--brand-blue)]/25 hover:opacity-95" loading={submitting}>
            {t('auth.sendResetLink')}
          </Button>

          <p className="text-center">
            <Link to="/" className="text-[13px] text-white/70 underline-offset-4 hover:text-white hover:underline">
              {t('auth.backToSignIn')}
            </Link>
          </p>
        </form>
      )}
    </AuthScreen>
  );
}
